'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type {
  PronunciationItem,
  PronunciationLesson,
  PronunciationScript
} from '@/content/pronunciation'
import { cn } from '@/lib/utils'
import { PronunciationAudioButton } from './pronunciation-audio-button'
import { PronunciationLessonStatus } from './pronunciation-progress-summary'
import { PronunciationQuiz } from './pronunciation-quiz'
import { useSettings } from '@/hooks/useSettings'
import { useChineseFont } from '@/lib/chinese-font'

export type PronunciationSoundSection = {
  id: string
  soundTag: string
  pinyin: string
  zhuyin: string
  audioPath?: string
  samplePinyin: string
  sampleZhuyin: string
  examples: PronunciationItem[]
}

type PronunciationLessonAccordionProps = {
  script: PronunciationScript
  lesson: PronunciationLesson
  soundSections: PronunciationSoundSection[]
}

const ZHUYIN_LESSON_TITLES: Record<string, string> = {
  'tones-vowels': 'Tones and basic vowels',
  bpmf: 'ㄅ ㄆ ㄇ ㄈ with simple finals',
  dtnl: 'ㄉ ㄊ ㄋ ㄌ and smooth transitions',
  gkh: 'ㄍ ㄎ ㄏ and back sounds',
  jqx: 'ㄐ ㄑ ㄒ and i/ü finals',
  zcs: 'ㄗ ㄘ ㄙ contrasts',
  zhchshr: 'ㄓ ㄔ ㄕ ㄖ retroflex sounds',
  nasals: 'Nasal finals and common contrasts',
  tones: 'Tone contrast practice',
  'mixed-review': 'Mixed real-word review'
}

export function PronunciationLessonAccordion({
  script,
  lesson,
  soundSections
}: PronunciationLessonAccordionProps) {
  const [open, setOpen] = useState(false)

  const displayTitle =
    script === 'zhuyin'
      ? ZHUYIN_LESSON_TITLES[lesson.id] || lesson.title
      : lesson.title

  return (
    <section
      id={`lesson-${lesson.id}`}
      className="bg-background/85 scroll-mt-24 overflow-hidden rounded-2xl border"
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="hover:bg-muted/35 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
        aria-expanded={open}
      >
        <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
          {lesson.order}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{displayTitle}</span>
          <span className="text-muted-foreground block text-xs">
            {soundSections.length} sounds
          </span>
        </span>
        <PronunciationLessonStatus script={script} lessonId={lesson.id} />
        <ChevronDown
          className={cn(
            'text-muted-foreground h-4 w-4 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t px-4 py-4">
            <div className="mb-5 flex justify-end">
              <PronunciationQuiz
                script={script}
                lessonId={lesson.id}
                title={lesson.title}
                buttonLabel="Practice"
              />
            </div>
            <div className="space-y-5">
              {soundSections.map((section) => (
                <SoundSection
                  key={section.id}
                  section={section}
                  script={script}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function SoundSection({
  section,
  script
}: {
  section: PronunciationSoundSection
  script: PronunciationScript
}) {
  const primary = script === 'pinyin' ? section.pinyin : section.zhuyin
  const secondary = script === 'pinyin' ? section.zhuyin : ''
  const sampleLine =
    script === 'pinyin'
      ? `Sample: ${section.samplePinyin} / ${section.sampleZhuyin}`
      : `Sample: ${section.sampleZhuyin}`

  return (
    <div className="border-b pb-5 last:border-b-0 last:pb-0">
      <div className="mb-3 flex items-center gap-3">
        <PronunciationAudioButton
          audioPath={section.audioPath}
          label={primary}
          compact
        />
        <div>
          <div className="flex items-baseline gap-2">
            <h3
              className={cn(
                'text-3xl font-semibold tracking-tight',
                script === 'zhuyin' && 'text-4xl'
              )}
            >
              {primary}
            </h3>
            {secondary && (
              <span className="text-muted-foreground text-sm">
                ({secondary})
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-0.5 text-xs">{sampleLine}</p>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {section.examples.map((example) => (
          <ExampleWord key={example.id} item={example} script={script} />
        ))}
      </div>
    </div>
  )
}

function ExampleWord({
  item,
  script
}: {
  item: PronunciationItem
  script: PronunciationScript
}) {
  const { settings } = useSettings()
  const chineseFont = useChineseFont()
  const primary = script === 'pinyin' ? item.pinyin : item.zhuyin
  const secondary = script === 'pinyin' ? item.zhuyin : ''
  const displayHanzi =
    settings.scriptPriority === 'traditional'
      ? item.traditional || item.hanzi
      : item.hanzi

  return (
    <div className="bg-muted/25 grid grid-cols-[2rem_3.5rem_minmax(0,1fr)] items-center gap-3 rounded-xl px-3 py-2">
      <PronunciationAudioButton
        audioPath={item.audioPath}
        label={primary}
        compact
      />
      <span className={cn(chineseFont, 'text-xl font-semibold')}>
        {displayHanzi}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{primary}</span>
        <span className="text-muted-foreground block truncate text-xs">
          {secondary ? `${secondary} · ` : ''}
          {item.meaning}
        </span>
      </span>
    </div>
  )
}
